import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Loader2, CheckCircle, Star, User, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const feedbackSchema = z.object({
  name: z.string().trim().min(1, "Your name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  heardFrom: z.string().min(1, "Please tell us how you heard about us"),
  rating: z.number().min(1, "Please select a rating").max(5),
  improvements: z.string().trim().max(1000).optional(),
  additionalComments: z.string().trim().max(1000).optional(),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface CustomerFeedbackDialogProps {
  children: React.ReactNode;
  defaultEmail?: string | null;
}

const HEARD_FROM_OPTIONS = [
  { value: "friend", label: "Friend or Family" },
  { value: "social_media", label: "Social Media" },
  { value: "store", label: "A Store's Website" },
  { value: "search", label: "Google Search" },
  { value: "ad", label: "Online Ad" },
  { value: "other", label: "Other" },
];

const IMPROVEMENT_OPTIONS = [
  { value: "ui", label: "User Interface / Design" },
  { value: "payment", label: "Payment Process" },
  { value: "speed", label: "Page Load Speed" },
  { value: "tracking", label: "Purchase Tracking" },
  { value: "notifications", label: "Notifications / Reminders" },
  { value: "mobile", label: "Mobile Experience" },
  { value: "other", label: "Other" },
];

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CustomerFeedbackDialog({ children, defaultEmail }: CustomerFeedbackDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: "",
    email: defaultEmail || "",
    heardFrom: "",
    rating: 0,
    improvements: "",
    additionalComments: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackFormData, string>>>({});

  const handleChange = (field: keyof FeedbackFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = feedbackSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FeedbackFormData, string>> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof FeedbackFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const heardFromLabel = HEARD_FROM_OPTIONS.find(o => o.value === formData.heardFrom)?.label || formData.heardFrom;
      
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: formData.name,
          email: formData.email,
          subject: `Customer Feedback - Rating: ${formData.rating}/5`,
          message: `Customer Feedback Received!\n\n` +
            `From: ${formData.name} (${formData.email})\n` +
            `Rating: ${"⭐".repeat(formData.rating)} (${formData.rating}/5)\n\n` +
            `How they heard about us: ${heardFromLabel}\n\n` +
            `Areas to improve: ${formData.improvements || "Not specified"}\n\n` +
            `Additional Comments:\n${formData.additionalComments || "None provided"}`,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Thank you! 💜",
        description: "Your feedback helps us improve Shakatah for everyone.",
      });
    } catch (err: any) {
      console.error("Error submitting feedback:", err);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: defaultEmail || "",
      heardFrom: "",
      rating: 0,
      improvements: "",
      additionalComments: "",
    });
    setErrors({});
    setIsSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) {
        setTimeout(resetForm, 300);
      }
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-customer" />
            Share Your Feedback
          </DialogTitle>
          <DialogDescription>
            Help us make group gifting even better!
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-customer/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-customer" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Thanks for your feedback! 💜
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              We truly appreciate you taking the time to help us improve.
            </p>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Your Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="w-4 h-4" />
                Your Details
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs">Name *</Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Rating */}
            <div className="space-y-1.5">
              <Label className="text-xs">How would you rate your experience? *</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleChange("rating", star)}
                    className={`p-1 rounded transition-colors ${
                      formData.rating >= star 
                        ? "text-yellow-500" 
                        : "text-muted-foreground/30 hover:text-yellow-400"
                    }`}
                  >
                    <Star className={`w-6 h-6 ${formData.rating >= star ? "fill-current" : ""}`} />
                  </button>
                ))}
              </div>
              {errors.rating && (
                <p className="text-xs text-destructive">{errors.rating}</p>
              )}
            </div>

            {/* How did you hear about us */}
            <div className="space-y-1.5">
              <Label htmlFor="heardFrom" className="text-xs">How did you hear about us? *</Label>
              <Select 
                value={formData.heardFrom} 
                onValueChange={(value) => handleChange("heardFrom", value)}
              >
                <SelectTrigger className={errors.heardFrom ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {HEARD_FROM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.heardFrom && (
                <p className="text-xs text-destructive">{errors.heardFrom}</p>
              )}
            </div>

            {/* Areas to improve */}
            <div className="space-y-1.5">
              <Label htmlFor="improvements" className="text-xs">What can we improve? (optional)</Label>
              <Select 
                value={formData.improvements} 
                onValueChange={(value) => handleChange("improvements", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an area" />
                </SelectTrigger>
                <SelectContent>
                  {IMPROVEMENT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Additional Comments */}
            <div className="space-y-1.5">
              <Label htmlFor="additionalComments" className="text-xs">Tell us more (optional)</Label>
              <Textarea
                id="additionalComments"
                placeholder="Any other thoughts, suggestions, or experiences you'd like to share..."
                value={formData.additionalComments}
                onChange={(e) => handleChange("additionalComments", e.target.value)}
                rows={3}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-customer hover:bg-customer/90 text-customer-foreground"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
